import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { companyApi } from '@/api';
import {
  ActionMenu,
  ActionMenuItem,
  Badge,
  BoxAvatar,
  BoxList,
  BoxRow,
  Button,
  CardActions,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  Icon,
  LinkButton,
  ListCard,
  LoadingState,
  PageHeader,
  Pagination,
  SegmentedTabs,
  useToast,
  ViewButton,
} from '@/components/ui';
import { INTERNSHIP_STATUS_LABELS, INTERNSHIP_STATUS_TONES } from '@/features/internships/status';
import { errorMessage } from '@/hooks/useApiError';
import { VerifyToContinueButton } from '@/features/verification/CompanyVerificationGate';
import { useCompanyVerification } from '@/features/verification/useCompanyVerification';
import { usePermissions } from '@/hooks/usePermissions';
import { paths } from '@/routes/paths';
import { PERMISSIONS } from '@/types/roles';
import { formatDate, stipendLabel } from '@/utils/format';

const TABS = [
  { key: '', label: 'All' },
  { key: 'DRAFT', label: 'Drafts' },
  { key: 'PENDING_APPROVAL', label: 'In review' },
  { key: 'PUBLISHED', label: 'Live' },
  { key: 'CLOSED', label: 'Closed' },
  { key: 'REJECTED', label: 'Rejected' },
  // Taken down by a Join-Wonder admin; otherwise only reachable under All.
  { key: 'ARCHIVED', label: 'Archived' },
];

const WORK_MODES = { REMOTE: 'Remote', HYBRID: 'Hybrid', ONSITE: 'On site' };

/** The date that matters for where the listing stands. */
function statusDate(row) {
  if (row.status === 'PUBLISHED' && row.published_at) {
    return `Published ${formatDate(row.published_at)}`;
  }
  if (row.status === 'PENDING_APPROVAL') return `Submitted ${formatDate(row.updated_at)}`;
  if (row.status === 'DRAFT') return `Created ${formatDate(row.created_at)}`;
  return `Updated ${formatDate(row.updated_at)}`;
}

const PAGE_SIZE = 20;
// Enough to say what the role is; the rest are on the listing's page.
const SHOWN_SKILLS = 6;

export function CompanyInternshipsPage() {
  const queryClient = useQueryClient();
  const { can } = usePermissions();
  // Each action shows only to members the API would let through.
  const canCreate = can(PERMISSIONS.INTERNSHIP_CREATE);
  const canUpdate = can(PERMISSIONS.INTERNSHIP_UPDATE);
  const canPublish = can(PERMISSIONS.INTERNSHIP_PUBLISH);
  const canDelete = can(PERMISSIONS.INTERNSHIP_DELETE);
  const { needsVerification } = useCompanyVerification();
  const canReadApplications = can(PERMISSIONS.APPLICATION_READ);
  const canSource = can(PERMISSIONS.CANDIDATE_READ);
  const navigate = useNavigate();
  const toast = useToast();
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const [pendingDeletion, setPendingDeletion] = useState(null);

  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: ['company-internships', status, page],
    queryFn: () =>
      companyApi.listInternships({
        page,
        page_size: PAGE_SIZE,
        ...(status ? { statuses: status } : {}),
      }),
  });

  const { data: counts } = useQuery({
    queryKey: ['company-internship-counts'],
    queryFn: companyApi.getInternshipCounts,
  });

  function invalidate() {
    void queryClient.invalidateQueries({ queryKey: ['company-internships'] });
    void queryClient.invalidateQueries({ queryKey: ['company-internship-counts'] });
  }

  const publish = useMutation({
    mutationFn: (id) => companyApi.publishInternship(id),
    onSuccess: (result) => {
      toast.success(result.message);
      invalidate();
    },
    onError: (publishError) =>
      toast.error(errorMessage(publishError, 'Could not publish the internship')),
  });

  const close = useMutation({
    mutationFn: (id) => companyApi.closeInternship(id),
    onSuccess: () => {
      toast.success('Internship closed');
      invalidate();
    },
    onError: (closeError) =>
      toast.error(errorMessage(closeError, 'Could not close the internship')),
  });

  const remove = useMutation({
    mutationFn: (id) => companyApi.deleteInternship(id),
    onSuccess: () => {
      toast.success('Internship deleted');
      invalidate();
      setPendingDeletion(null);
    },
    onError: (deleteError) => {
      toast.error(errorMessage(deleteError, 'Could not delete the internship'));
      setPendingDeletion(null);
    },
  });

  const rows = data?.data ?? [];
  const meta = data?.meta;

  function tabCount(key) {
    if (!counts) return null;
    if (!key) return Object.values(counts).reduce((total, value) => total + value, 0);
    return counts[key] ?? 0;
  }

  const tabs = TABS.map((tab) => ({ ...tab, count: tabCount(tab.key) ?? undefined }));

  // Posting waits for verification, so the way in says that instead.
  const newInternship =
    canCreate &&
    (needsVerification ? (
      <VerifyToContinueButton />
    ) : (
      <Button onClick={() => navigate(paths.company.internshipNew)}>
        <Icon name="plus" size={16} />
        New internship
      </Button>
    ));

  return (
    <div>
      <PageHeader
        title="Internships"
        description="Draft, publish and close your postings."
        action={newInternship}
      />

      <ListCard
        toolbarStart={
          <SegmentedTabs
            variant="plain"
            label="Internship status"
            tabs={tabs}
            value={status}
            onChange={(key) => {
              setStatus(key);
              setPage(1);
            }}
          />
        }
        state={
          isError ? (
            <ErrorState message={errorMessage(error)} onRetry={() => void refetch()} />
          ) : isPending ? (
            <LoadingState label="Loading your internships…" />
          ) : rows.length === 0 ? (
            <EmptyState
              icon={<Icon name="internship" size={22} />}
              title="Nothing here yet"
              description={
                status
                  ? 'No internships in this state.'
                  : 'Create your first internship to start receiving applications.'
              }
              action={newInternship}
            />
          ) : null
        }
        footer={
          meta && (
            <Pagination
              className=""
              page={meta.page}
              pageSize={PAGE_SIZE}
              totalPages={meta.total_pages}
              totalItems={meta.total_items}
              onPageChange={setPage}
            />
          )
        }
      >
        <BoxList>
          {rows.map((row) => (
            <BoxRow
              key={row.id}
              accent={INTERNSHIP_STATUS_TONES[row.status] ?? 'neutral'}
              leading={<BoxAvatar name={row.title} />}
              title={row.title}
              to={paths.company.internshipDetail(row.id)}
              badges={
                <Badge dot tone={INTERNSHIP_STATUS_TONES[row.status] ?? 'neutral'}>
                  {INTERNSHIP_STATUS_LABELS[row.status] ?? row.status}
                </Badge>
              }
              subtitle={row.job_category}
              facts={[
                { icon: 'home', text: row.location ?? 'No location' },
                row.work_mode && {
                  icon: 'briefcase',
                  text: WORK_MODES[row.work_mode] ?? row.work_mode,
                },
                {
                  icon: 'users',
                  text: `${row.positions} ${row.positions === 1 ? 'position' : 'positions'}`,
                },
                // Only when there is an amount to show: an unpaid listing or a
                // draft without figures yet gets no chip.
                row.is_paid &&
                  (row.stipend_min != null || row.stipend_max != null) && {
                    key: 'stipend',
                    icon: 'banknote',
                    text: stipendLabel(row),
                  },
                {
                  icon: 'clockOutline',
                  text: row.application_deadline
                    ? `Deadline ${formatDate(row.application_deadline)}`
                    : 'No deadline',
                },
                { key: 'date', icon: 'calendar', text: statusDate(row) },
              ]}
              description={row.summary}
              actions={
                <CardActions>
                  {canPublish &&
                    !needsVerification &&
                    ['DRAFT', 'REJECTED', 'CLOSED'].includes(row.status) && (
                      <Button
                        variant="tintPositive"
                        size="sm"
                        title={`Submit ${row.title} for review`}
                        isLoading={publish.isPending && publish.variables === row.id}
                        onClick={() => publish.mutate(row.id)}
                      >
                        <Icon name="check" size={14} />
                        Submit for review
                      </Button>
                    )}
                  {canUpdate && (
                    <LinkButton
                      to={paths.company.internshipEdit(row.id)}
                      variant="secondary"
                      size="sm"
                      title={`Edit ${row.title}`}
                    >
                      <Icon name="pencil" size={14} />
                      Edit
                    </LinkButton>
                  )}
                  {/* The people who applied, on the Applications page filtered
                      to this listing. Only a listing that has been live can
                      have any. Finding candidates who have not applied stays
                      in "More". */}
                  {canReadApplications && row.published_at && (
                    <LinkButton
                      to={`${paths.company.applications}?internship=${row.id}`}
                      variant="secondary"
                      size="sm"
                      title={`Applications for ${row.title}`}
                    >
                      <Icon name="users" size={14} />
                      Applicants
                    </LinkButton>
                  )}
                  {/* Performance and sourcing are occasional: they wait in
                      "More" so the footer stays at the everyday actions. */}
                  <ActionMenu label={`More actions for ${row.title}`}>
                    {/* A listing that never went live has no audience to report on. */}
                    {row.published_at && (
                      <ActionMenuItem
                        icon="chartBar"
                        to={paths.company.internshipPerformance(row.id)}
                      >
                        View performance
                      </ActionMenuItem>
                    )}
                    {/* Sourcing only makes sense for a listing people can
                        actually be pointed at. */}
                    {canSource && row.status === 'PUBLISHED' && (
                      <ActionMenuItem icon="users" to={paths.company.internshipSourcing(row.id)}>
                        Find candidates
                      </ActionMenuItem>
                    )}
                  </ActionMenu>
                  <ViewButton
                    to={paths.company.internshipDetail(row.id)}
                    title={`Open "${row.title}"`}
                  />
                </CardActions>
              }
              // Top right, apart from the everyday actions, as Withdraw is on a
              // student's application: closing and deleting end the listing.
              corner={
                <>
                  {canPublish && ['PUBLISHED', 'PENDING_APPROVAL'].includes(row.status) && (
                    <Button
                      variant="ghostDanger"
                      size="sm"
                      title={`Close ${row.title}`}
                      isLoading={close.isPending && close.variables === row.id}
                      onClick={() => close.mutate(row.id)}
                    >
                      <Icon name="archive" size={14} />
                      Close listing
                    </Button>
                  )}
                  {canDelete && row.status !== 'PUBLISHED' && (
                    <Button
                      variant="ghostDanger"
                      size="sm"
                      title={`Delete ${row.title}`}
                      onClick={() => setPendingDeletion(row)}
                    >
                      <Icon name="trash" size={14} />
                      Delete
                    </Button>
                  )}
                </>
              }
            >
              {row.skills?.length > 0 && (
                <ul className="mt-3 flex flex-wrap gap-1.5" aria-label={`Skills for ${row.title}`}>
                  {row.skills.slice(0, SHOWN_SKILLS).map((skill) => (
                    <li key={skill}>
                      <Badge tone="brand">{skill}</Badge>
                    </li>
                  ))}
                  {row.skills.length > SHOWN_SKILLS && (
                    <li>
                      <Badge tone="neutral">+{row.skills.length - SHOWN_SKILLS} more</Badge>
                    </li>
                  )}
                </ul>
              )}
            </BoxRow>
          ))}
        </BoxList>
      </ListCard>
      

      <ConfirmDialog
        open={Boolean(pendingDeletion)}
        title="Delete internship"
        message={`"${pendingDeletion?.title}" will be removed. This cannot be undone.`}
        confirmLabel="Delete"
        isLoading={remove.isPending}
        onConfirm={() => remove.mutate(pendingDeletion.id)}
        onCancel={() => setPendingDeletion(null)}
      />
    </div>
  );
}
